import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { colors, radius, shadows } from '../../styles/theme';
import { exportCsv } from '../../utils/analytics/export';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const toDateInput = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatSure = (seconds) => {
  const s = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sn = s % 60;
  if (h > 0) return `${h} sa ${m} dk`;
  if (m > 0) return `${m} dk ${sn} sn`;
  return `${sn} sn`;
};

export default function KullaniciHareketRaporuPage() {
  const now = useMemo(() => new Date(), []);
  const sixDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d;
  }, []);

  const [baslangic, setBaslangic] = useState(toDateInput(sixDaysAgo));
  const [bitis, setBitis] = useState(toDateInput(now));
  const [data, setData] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        baslangic,
        bitis,
        limit: '5000',
      });

      const res = await fetch(`${API_URL}/api/page-usage/report?${params.toString()}`, {
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Rapor yüklenemedi');
      }

      setData(await res.json());
    } catch (err) {
      setData(null);
      setError(err.message || 'Rapor yüklenemedi');
      logError('ERP kullanım raporu:', err);
    } finally {
      setLoading(false);
    }
  }, [baslangic, bitis]);

  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  const userGroups = useMemo(() => {
    const groups = new Map();

    (data?.kayitlar || [])
      .filter((x) => Number(x.active_seconds || 0) > 0)
      .forEach((x) => {
        const key = String(x.kullanici_id);
        if (!groups.has(key)) {
          groups.set(key, {
            id: x.kullanici_id,
            ad: x.kullanici_ad || x.kullanici_email || `Kullanıcı #${x.kullanici_id}`,
            email: x.kullanici_email || '',
            totalSeconds: 0,
            visits: 0,
            pages: new Map(),
          });
        }

        const user = groups.get(key);
        user.totalSeconds += Number(x.active_seconds || 0);
        user.visits += 1;

        const pageKey = x.page_path || x.page_name || 'BILINMEYEN';
        if (!user.pages.has(pageKey)) {
          user.pages.set(pageKey, {
            pagePath: x.page_path,
            pageName: x.page_name || x.page_path || 'Bilinmeyen Sayfa',
            visits: 0,
            totalSeconds: 0,
          });
        }

        const page = user.pages.get(pageKey);
        page.visits += 1;
        page.totalSeconds += Number(x.active_seconds || 0);
      });

    return [...groups.values()]
      .map((u) => ({
        ...u,
        pages: [...u.pages.values()].sort((a, b) => b.totalSeconds - a.totalSeconds),
      }))
      .sort((a, b) => b.totalSeconds - a.totalSeconds);
  }, [data]);

  const toplamAktifSure = userGroups.reduce((sum, u) => sum + u.totalSeconds, 0);

  const toggleUser = (id) => {
    const key = String(id);
    setExpandedUsers((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const handleCsv = () => {
    const rows = [];
    userGroups.forEach((u) => {
      u.pages.forEach((p) => {
        rows.push({
          Kullanıcı: u.ad,
          'E-Posta': u.email,
          Sayfa: p.pageName,
          Ziyaret: p.visits,
          'Toplam Süre': formatSure(p.totalSeconds),
          'Toplam Saniye': p.totalSeconds,
        });
      });
    });
    exportCsv(rows, `erp-kullanim-raporu-${baslangic}-${bitis}.csv`);
  };

  const containerStyle = { padding: 20, maxWidth: 1150, margin: '0 auto' };
  const mainCardStyle = {
    background: colors.surface,
    borderRadius: radius.lg,
    padding: 20,
    boxShadow: shadows.sm,
  };
  const inputStyle = {
    padding: '10px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    background: '#fff',
    fontSize: 14,
  };
  const userCardStyle = {
    background: '#fff',
    borderRadius: 14,
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    marginBottom: 12,
  };
  const userHeaderStyle = {
    width: '100%',
    padding: 16,
    border: 0,
    background: '#fff',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    cursor: 'pointer',
  };
  const th = {
    padding: 10,
    textAlign: 'left',
    fontSize: 13,
    color: '#334155',
    borderBottom: '1px solid #e2e8f0',
  };
  const td = {
    padding: 10,
    borderBottom: '1px solid #e2e8f0',
    fontSize: 14,
  };

  return (
    <div style={containerStyle}>
      <div style={mainCardStyle}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 18,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, color: '#1e293b' }}>ERP Kullanım Raporu</h2>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
              Kullanıcıların ERP'de aktif olarak geçirdiği süreler
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, color: '#64748b' }}>
              Başlangıç
              <br />
              <input type="date" style={inputStyle} value={baslangic} onChange={(e) => setBaslangic(e.target.value)} />
            </label>
            <label style={{ fontSize: 13, color: '#64748b' }}>
              Bitiş
              <br />
              <input type="date" style={inputStyle} value={bitis} onChange={(e) => setBitis(e.target.value)} />
            </label>
            <button
              type="button"
              onClick={fetchRapor}
              disabled={loading}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: 0,
                background: loading ? '#cbd5e1' : '#2563eb',
                color: '#fff',
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Yükleniyor...' : 'Listele'}
            </button>
            <button
              type="button"
              onClick={handleCsv}
              disabled={!userGroups.length}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: '1px solid #15803d',
                background: userGroups.length ? '#16a34a' : '#e2e8f0',
                color: userGroups.length ? '#fff' : '#64748b',
                fontWeight: 700,
                cursor: userGroups.length ? 'pointer' : 'not-allowed',
              }}
            >
              CSV İndir
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: 12, borderRadius: 10, background: '#fee2e2', color: '#991b1b', marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}>
          <div style={{ padding: 16, borderRadius: 14, background: '#f0f9ff' }}>
            <div style={{ fontSize: 12, color: '#64748b' }}>Toplam ERP Kullanımı</div>
            <div style={{ fontSize: 25, fontWeight: 800, color: '#1e40af', marginTop: 3 }}>
              {formatSure(toplamAktifSure)}
            </div>
          </div>
          <div style={{ padding: 16, borderRadius: 14, background: '#f0fdf4' }}>
            <div style={{ fontSize: 12, color: '#64748b' }}>Kullanıcı Sayısı</div>
            <div style={{ fontSize: 25, fontWeight: 800, color: '#166534', marginTop: 3 }}>
              {userGroups.length}
            </div>
          </div>
        </div>

        {loading && !data ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Veriler hazırlanıyor...</div>
        ) : userGroups.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
            Seçilen dönemde aktif kullanım kaydı bulunamadı.
          </div>
        ) : (
          <div>
            {userGroups.map((user) => {
              const key = String(user.id);
              const expanded = Boolean(expandedUsers[key]);

              return (
                <div key={key} style={userCardStyle}>
                  <button type="button" onClick={() => toggleUser(user.id)} style={userHeaderStyle}>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
                        {expanded ? '▼' : '▶'} {user.ad}
                      </div>
                      <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                        {user.visits} ziyaret · {user.pages.length} farklı sayfa
                      </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Toplam kullanım</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#1e40af' }}>
                        {formatSure(user.totalSeconds)}
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f1f5f9' }}>
                          <tr>
                            <th style={th}>Sayfa</th>
                            <th style={{ ...th, textAlign: 'right' }}>Ziyaret</th>
                            <th style={{ ...th, textAlign: 'right' }}>Toplam Süre</th>
                          </tr>
                        </thead>
                        <tbody>
                          {user.pages.map((page) => (
                            <tr key={page.pagePath || page.pageName}>
                              <td style={td}>
                                <b>{page.pageName}</b>
                              </td>
                              <td style={{ ...td, textAlign: 'right' }}>{page.visits}</td>
                              <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#1e40af' }}>
                                {formatSure(page.totalSeconds)}
                              </td>
                            </tr>
                          ))}
                          <tr style={{ background: '#f8fafc' }}>
                            <td style={{ ...td, fontWeight: 800 }}>TOPLAM</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{user.visits}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>
                              {formatSure(user.totalSeconds)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
